import { createContext, useContext, type ReactNode } from "react";

import type { PermissionDecision } from "./PermissionGate";

/**
 * The decision `PermissionGate` was given, for the two components below and
 * nothing else. `null` outside a gate, which both components treat as "not
 * allowed" (I-1): a block rendered with no gate above it has not been told
 * the person may see it.
 */
export const PermissionDecisionContext = createContext<PermissionDecision | null>(null);

/**
 * A block that ONLY writes — a decision form, a submit panel — rendered for an
 * `edit` decision and NOT rendered at all for anyone else. Absent, not inert:
 * no inputs, no effects, nothing in the tab order (bug 3f80402f-8134).
 *
 * Use it only for a block with nothing a viewer needs to read. A block that
 * mixes reading and writing stays rendered and marks its read-only controls
 * `data-nonmutating`, so read-only never becomes no-access (UX S5).
 *
 * Shell-owned (I-7): a module imports it and never reads the decision itself.
 */
export function WriteOnly({ children }: { children: ReactNode }) {
  const decision = useContext(PermissionDecisionContext);
  return decision === "edit" ? <>{children}</> : null;
}

/**
 * What a viewer reads where a `WriteOnly` block would be — "Waiting for
 * Damien's decision." — so a read-only pane never ends in blank space.
 * Rendered for a `view` decision only.
 */
export function ViewOnly({ children }: { children: ReactNode }) {
  const decision = useContext(PermissionDecisionContext);
  return decision === "view" ? <>{children}</> : null;
}
